import api from "@/plugins/axios";
import { toast } from "vue3-toastify";

// Normalize a single cell: numbers stay numbers (so the backend writes real
// numeric cells in German format), everything else becomes a string.
function normalizeCell(value) {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(normalizeCell).join(" / ");
  }
  if (typeof value === "object") {
    if ("x" in value || "y" in value || "r" in value) {
      return ["x", "y", "r"]
        .filter((key) => value[key] !== undefined)
        .map((key) => `${key}=${value[key]}`)
        .join("; ");
    }
    return JSON.stringify(value);
  }
  return value;
}

// POST a generic { headers, rows } table to the backend and download the
// returned real .xlsx (content matches extension -> no Excel format warning).
async function postTableExcel(projectId, { headers, rows, filePrefix, sheetName = "Daten" }) {
  if (!projectId) {
    toast.error("Projekt-ID fehlt.");
    return;
  }
  try {
    const response = await api.post(
      `/projects/${projectId}/chart_excel/`,
      {
        filename: `${filePrefix}-daten-projekt-${projectId}`,
        sheetName,
        headers,
        rows,
      },
      { responseType: "blob" },
    );

    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    const disposition = response.headers?.["content-disposition"] || "";
    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    link.download =
      match?.[1]?.replace(/['"]/g, "") || `${filePrefix}-daten-projekt-${projectId}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Excel-Export konnte nicht erstellt werden.");
  }
}

// Export a Chart.js dataset ({ labels, datasets }) as a real .xlsx.
export async function exportChartExcel(projectId, chartData, filePrefix) {
  const labels = chartData?.labels || [];
  const datasets = chartData?.datasets || [];
  const headers = ["Label", ...datasets.map((dataset, index) => dataset?.label || `Datensatz ${index + 1}`)];
  const rowCount = Math.max(labels.length, ...datasets.map((dataset) => (dataset?.data || []).length), 0);
  const rows = Array.from({ length: rowCount }, (_, index) => [
    labels[index] ?? `Index ${index + 1}`,
    ...datasets.map((dataset) => normalizeCell((dataset?.data || [])[index])),
  ]);
  return postTableExcel(projectId, { headers, rows, filePrefix });
}

// Export Sankey link data ({ links: [{ source, target, value }] }) as a real .xlsx.
export async function exportSankeyExcel(projectId, sankeyData, filePrefix) {
  const links = Array.isArray(sankeyData?.links) ? sankeyData.links : [];
  const headers = ["Quelle", "Ziel", "Wert [kWh]"];
  const rows = links.map((link) => [link?.source || "", link?.target || "", Number(link?.value || 0)]);
  return postTableExcel(projectId, { headers, rows, filePrefix });
}
